import React, { useState }  from 'react';
import { shallowEqual } from 'react-redux';
import QuizManager from '../../../../classes/QuizManager';
import { ChecklistType, QuizAnswerStatusColor, QuizQuestionStatus, SimulationStates } from '../../../../globals';
import Spacer from '../../../layout/Spacer';
import { useTypedSelector } from '../../../../state/hooks';

interface MultioptionProps {
	id: string; // Unique identifier for the component
	pointerEvents?: string; // Optional: Defines CSS pointer-events for the component
	item: {
		type: ChecklistType; // The type of the checklist item
	};
	options: {
		description: string; // Text description for the option
		correct?: boolean; // Whether the option is correct
	}[]; // List of options
	answers?: boolean[]; // Boolean array indicating selected answers
	attempts?: number; // Number of attempts left
	onChangeCallback?: (index: number, checked: boolean) => void; // Callback triggered on answer selection
}

export default function Multioption(props: MultioptionProps)
{
	const simulationMode = useTypedSelector(state => state.applicationModule.simulationState, shallowEqual);

	const style: React.CSSProperties = {
		pointerEvents: props.pointerEvents as React.CSSProperties['pointerEvents'] || 'none',
		// paddingLeft:'20px',
		paddingRight:'20px'
	}

	const [value, setValue] = useState(-1);
	const inputType = (()=>
	{
		if(props.item.type === ChecklistType.MULTI_OPTION) return "radio";
		if(props.item.type === ChecklistType.MULTI_OPTION_SELECT) return "checkbox";
	})();

	function buildStyle(index)
	{
		if(QuizManager.getAnswerStatus() && props.answers)
		{
			if(QuizManager.getAnswerStatus() === QuizQuestionStatus.UNANSWERED) return {};

			if(QuizManager.getAnswerStatus() === QuizQuestionStatus.CORRECT)
			{
				if(props.answers[index] === true && props.options[index].correct === true)
				{
					return { color: QuizAnswerStatusColor.CORRECT };
				}

				return { opacity:'0.4' };
			}

			if(QuizManager.getAnswerStatus() === QuizQuestionStatus.INCORRECT)
			{
				if(props.attempts === 0)
				{
					if(props.answers[index] === true)
					{
						if(props.options[index].correct !== true)
						{
							return { color: QuizAnswerStatusColor.INCORRECT };
						}
						else
						{
							return { color: QuizAnswerStatusColor.CORRECT };
						}
					}
					else if(QuizManager.shouldRevealAnswers())
					{
						if(props.options[index].correct === true)
						{
							return { opacity:'1' };
						}

						return { opacity:'0.4' };
					}
				}
				else
				{
					if(props.item.type !== ChecklistType.MULTI_OPTION_SELECT_3D && props.item.type !== ChecklistType.MULTI_OPTION_SELECT)
					{
						if(props.answers[index] === true)
						{
							return { color: QuizAnswerStatusColor.INCORRECT };
						}

						return { opacity:'0.4' };
					}

					if (props.item.type === ChecklistType.MULTI_OPTION_SELECT_3D)
					{
						return { paddingLeft: '0px'}
					}

					if(props.answers[index] === true)
					{
						return { opacity:'1' };
					}
					return { opacity:'0.4' };
				}
			}
		}
	}

	function buildMarkType(index)
	{
		if(QuizManager.getAnswerStatus() && props.answers)
		{

			if(QuizManager.getAnswerStatus() === QuizQuestionStatus.CORRECT)
			{
				if(props.answers[index] === true && props.options[index].correct === true)
				{
					return 'greencheckmark'; //correct
				}

				return 'hideborder';
			}

			if(QuizManager.getAnswerStatus() === QuizQuestionStatus.INCORRECT)
			{
				if(props.attempts === 0)
				{
					if(props.answers[index] === true)
					{
						if(props.options[index].correct !== true)
						{
							return 'redcheckmark'; //incorrect
						}
						else //selected ansewr is partially correct
						{
							return 'greencheckmark'; //correct
						}
					}
					else //answer is not selected
					{
						return 'hideborder';
					}
				}
				else //still has attempts
				{
					if(props.item.type !== ChecklistType.MULTI_OPTION_SELECT_3D && props.item.type !== ChecklistType.MULTI_OPTION_SELECT)
					{
						return 'redcheckmark'; //incorrect
					}

					if (props.item.type === ChecklistType.MULTI_OPTION_SELECT_3D)
					{
						return 'hideborder';
					}

					if (props.item.type === ChecklistType.MULTI_OPTION_SELECT)
					{
						if(props.answers[index] === true)
						{
							return 'disabledgraycheckmark';
						}
						return 'disableborder';
					}
				}
			}
		}

		if (props.item.type === ChecklistType.MULTI_OPTION_SELECT)
		{
			return 'graycheckmark'; //
		}
		return 'normal';
	}

	const generatedOptions = (() =>
	{
		const options: JSX.Element[] = [];
		for(let i = 0; i< props.options.length; i++)
		{
			if(props.item.type === ChecklistType.MULTI_OPTION_3D || props.item.type === ChecklistType.MULTI_OPTION_SELECT_3D)
			{
				if(simulationMode !== SimulationStates.Playing)
				{
					options.push(
						<label key={i}
							className={"optcontainer-3d-answers"}
							style={buildStyle(i)}
						>
							{`${String.fromCharCode(65 + (i))}) ${props.options[i].description}`}
						</label>
					);
					if(i < props.options.length - 1)
					{
						options.push(
							<Spacer key={i+'_spacer'} size={{ y : '8px' }}/>
						);
					}
				}
				else
				{
					if(QuizManager.getAnswerStatus() === QuizQuestionStatus.UNANSWERED)
					{
						if(props.answers && props.answers[i] === true)
						{
							options.push(
								<label key={i}
									className={"optcontainer-3d-answers"}
									style={buildStyle(i)}
								>
									{`${String.fromCharCode(65 + (i))}`}
								</label>
							);
							if(i < props.options.length - 1)
							{
								options.push(
									<Spacer key={i+'_spacer'} size={{ y : '8px' }}/>
								);
							}
						}
					}
					else
					{
						if(QuizManager.getAnswerStatus() === QuizQuestionStatus.INCORRECT && props.answers && props.answers[i] !== true)
						{
							if(props.attempts !== 0 || (props.attempts === 0 && !QuizManager.shouldRevealAnswers()))
							{
								continue;
							}
						}

						if(QuizManager.getAnswerStatus() === QuizQuestionStatus.INCORRECT && props.item.type === ChecklistType.MULTI_OPTION_SELECT_3D && props.attempts !== 0)
						{
							options.push(
								<label key={i}
									className={"optcontainer"}
									style={buildStyle(i)}
								>
									{`${String.fromCharCode(65 + (i))}`}
									<span
										className='checkmark-3d-answer'
										data-marktype={buildMarkType(i)}
									/>
								</label>
							);
						}
						else
						{
							options.push(
								<label key={i}
									className={"optcontainer"}
									style={buildStyle(i)}
								>
									{`${String.fromCharCode(65 + (i))}) ${props.options[i].description}`}
									<span
										className='checkmark-3d-answer'
										data-marktype={buildMarkType(i)}
									/>
								</label>
							);
						}


						if(i < props.options.length - 1)
						{
							options.push(
								<Spacer key={i+'_spacer'} size={{ y : '8px' }}/>
							);
						}
					}
				}
			}
			else
			{
				options.push(
					<label key={i}
						className={[
							"optcontainer",
							(props.item.type === ChecklistType.MULTI_OPTION ? "optcontainerRadio" : ""),
						].join(' ')}
						data-marktype={buildMarkType(i)}
						style={buildStyle(i)}
					>
						{props.options[i].description}
						<input
							type={ inputType }
							name={"option"+props.id}
							value={value}
							disabled={QuizManager.getAnswerStatus() !== QuizQuestionStatus.UNANSWERED}
							checked={props.answers && props.answers[i] ? true : undefined}
							onChange={(e) =>
							{
								setValue(i);
								if (props.onChangeCallback)
								{
									props.onChangeCallback(i, e.target.checked);
								}
							}}
						/>
						<span
							className={[
								"checkmark",
								(props.item.type === ChecklistType.MULTI_OPTION ? "checkmarkRadio" : ""),
							].join(' ')}
							data-marktype={buildMarkType(i)}
							data-ischecked={QuizManager.getAnswerStatus() === QuizQuestionStatus.UNANSWERED ? props.answers && props.answers[i] ? true : false : false}
						></span>
					</label>
				);
				if(i < props.options.length - 1)
				{
					options.push(
						<Spacer key={i+'_spacer'} size={{ y : '8px' }}/>
					);
				}
			}
		}
		return options;
	})()

	return (
		<div style={style}>
			{generatedOptions}
		</div>

	)
}
